import React, { useState, useEffect, useRef } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CircularProgress } from "@mui/material";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import ViewHsnReport from "./ViewHsnReport";
import { useAuth } from "../../../context/AuthProvider";
import { Link } from "react-router-dom";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import { ChevronRightIcon, DownloadIcon, SpreadSheetIcon } from "../../../constants/Icons";

const HsnReport = () => {
  const [errors, setErrors] = useState("");

  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const serverTime = GetServerLiveTime();

  var obj = {
    label: isUser.title,
    value: isUser.storeId,
    stype: isUser.stype,
  };

  const [isPreLoading, setPreLoading] = useState(true);
  const [isbtnLoading, setButtonLoading] = useState(false);

  const [options, setOptions] = useState([]);
  const [isSelectedStore, setSelectedStore] = useState(obj);
  const storeSelectedRef = useRef(null);

  const [isHsnReportData, setHsnReportData] = useState([]);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const currentDate = new Date();
  const previousDate = new Date(serverTime.getTime() - 1000);
  const [fromDate, setFromDate] = useState(previousDate);
  const [toDate, setToDate] = useState(serverTime);

  const calculateDate = (baseDate, offsetDays) => {
    const resultDate = new Date(baseDate);
    resultDate.setDate(resultDate.getDate() + offsetDays);
    return resultDate;
  };

  const handleFromDateChange = (newValue) => {
    if (!newValue) return;
    const selectedFrom = newValue.startOf("day");

    const maxAllowedTo = selectedFrom.add(64, "days");
    const finalMaxTo = maxAllowedTo.isAfter(dayjs(serverTime))
      ? dayjs(serverTime)
      : maxAllowedTo;

    setFromDate(selectedFrom.toDate());
    if (
      dayjs(toDate).isBefore(selectedFrom) ||
      dayjs(toDate).isAfter(finalMaxTo)
    ) {
      setToDate(finalMaxTo.toDate());
    }
  };

  const handleToDateChange = (newValue) => {
    if (!newValue) return;
    const selectedTo = newValue.endOf("day");

    if (selectedTo.isBefore(dayjs(fromDate))) {
      setFromDate(selectedTo.startOf("day").toDate());
    }
    setToDate(selectedTo.toDate());
  };

  const toDateMin = fromDate;
  const toDateMax = dayjs(fromDate).add(64, "day").isAfter(dayjs(serverTime))
    ? dayjs(serverTime)
    : dayjs(fromDate).add(64, "day");

  const handleListStore = async () => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`list-store`);
      const options = formatOptions(response.data.results, isUser.stype);
      setOptions(options);
      const userStore = options.find(
        (option) => option.value === isUser.storeId
      );
      setSelectedStore(userStore);
      handleHsnReport();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const formatOptions = (data, userType) => {
    if (userType === "MAIN") {
      const mainOption = {
        label: data.title,
        value: data._id,
        stype: "MAIN",
      };

      const subOptions = data.subs.map((subStore) => ({
        label: subStore.title,
        value: subStore._id,
        stype: "SUB",
      }));

      const hubOptions = data.subs.reduce((acc, subStore) => {
        if (subStore.hubs && subStore.hubs.length > 0) {
          const hubs = subStore.hubs.map((hub) => ({
            label: hub.title,
            value: hub._id,
            stype: "HUB",
          }));
          return acc.concat(hubs);
        }
        return acc;
      }, []);

      return [mainOption, ...subOptions, ...hubOptions];
    }

    if (userType === "SUB") {
      const subOption = {
        label: data.title,
        value: data._id,
        stype: "SUB",
      };

      let hubOptions = [];
      if (data.hubs && data.hubs.length > 0) {
        hubOptions = data.hubs.map((hub) => ({
          label: hub.title,
          value: hub._id,
          stype: "HUB",
        }));
      }

      return [subOption, ...hubOptions];
    }

    if (userType == "HUB") {
      const hubOptions = {
        label: data.title,
        value: data._id,
        stype: "HUB",
      };
      return [hubOptions];
    }
    return [];
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const renderOptionLabel = ({ label, stype }) => {
    let marginRight;
    let fontWeight;
    let badgePadding;
    let fontSize;

    if (stype === "MAIN") {
      marginRight = "0px";
      fontWeight = "500";
      fontSize = "14.5px";
      badgePadding = "2px 8px";
    } else if (stype === "SUB") {
      marginRight = "2px";
      fontWeight = "400";
      fontSize = "14px";
      badgePadding = "2px 12px";
    } else if (stype === "HUB") {
      marginRight = "4px";
      fontWeight = "300";
      fontSize = "13.5px";
      badgePadding = "2px 14px";
    }

    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          marginLeft: marginRight,
        }}
      >
        <span
          className={`badge rounded-pill ${getColor(stype)}`}
          style={{
            marginRight: "5px",
            padding: badgePadding,
          }}
        >
          {stype}
        </span>
        <span style={{ fontWeight, fontSize }}>- {label}</span>
      </span>
    );
  };

  const handleSelectStore = (selected) => {
    setSelectedStore(selected);
  };

  const validateForm = () => {
    const errors = {};
    if (!isSelectedStore) {
      errors.store = Messages.ERROR.EMPTY_STORE;
    }
    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      switch (firstErrorField) {
        case "store":
          storeSelectedRef.current?.focus();
          break;
        default:
          break;
      }
    }
    return Object.keys(errors).length === 0;
  };

  const handleHsnReport = async () => {
    if (!validateForm()) return;
    setPreLoading(true);
    setButtonLoading(true);
    try {
      if (!checkNetwork()) return;

      var data = {
        storeid: isSelectedStore.value,
        fromdate: encodeURIComponent(
          DateConverter.convertToTimeZone(
            fromDate.toISOString(),
            isUser.timezone
          )
        ),
        todate: encodeURIComponent(
          DateConverter.convertToTimeZone(toDate.toISOString(), isUser.timezone)
        ),
      };

      const response = await AxiosInstance.get(
        `module/reports/hsn?fromdate=${data.fromdate}&todate=${data.todate}`
      );
      setHsnReportData(response.data.results);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
      setButtonLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!isHsnReportData?.reportData?.length) {
      showAlert("warning", "No data available to export");
      return;
    }

    const headers = `S.No,HSN Code,Tax(%),Quantity,Base Amount,SGST Amount,CGST Amount,IGST Amount,Tax Amount,Total Amount\n`;

    const clean = (val) => (val ?? "").toString().replace(/[\r\n;]/g, " ");

    const rows = isHsnReportData.reportData
      .map((item, index) => {
        return [
          index + 1,
          clean(item.hsnsac),
          clean(item.taxpercentage),
          clean(item.quantity),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalBasePrice || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalSgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalCgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalIgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalTaxAmount || 0, "INR")
          ),
          clean(CurrencyConverter.toCurrency(item.lineGrandTotal || 0, "INR")),
        ].join(",");
      })
      .join("\n");

    // Main Total Row
    const totalRow = [
      "",
      "TOTAL",
      "",
      "",
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.totalBasePrice,
        "INR"
      )}`,
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.totalSgstAmount,
        "INR"
      )}`,
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.totalCgstAmount,
        "INR"
      )}`,
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.totalIgstAmount,
        "INR"
      )}`,
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.totalTaxAmount,
        "INR"
      )}`,
      `${CurrencyConverter.toCurrency(
        isHsnReportData.reportSummary.grandTotal,
        "INR"
      )}`,
    ].join(",");

    // Tax Overview Section
    let summaryBlock = "";
    if (isHsnReportData?.taxSummary?.length > 0) {
      const summaryHeader =
        "\n\nTax %,Tax Amount,SGST Amount,CGST Amount,IGST Amount";

      const taxSummary = isHsnReportData.taxSummary || [];

      // Rows with taxpercentage > 0
      const summaryRows = taxSummary
        .filter((item) => Number(item?.taxpercentage) > 0) // exclude 0% and totals
        .map((item) => {
          return [
            `${item.taxpercentage}%`,
            CurrencyConverter.toCurrency(
              Number(item?.totalTaxAmount) || 0,
              "INR"
            ),
            CurrencyConverter.toCurrency(
              Number(item?.totalSgstAmount) || 0,
              "INR"
            ),
            CurrencyConverter.toCurrency(
              Number(item?.totalCgstAmount) || 0,
              "INR"
            ),
            CurrencyConverter.toCurrency(
              Number(item?.totalIgstAmount) || 0,
              "INR"
            ),
          ].join(",");
        });

      // ✅ Totals row from the last array item
      let totalRow = "";
      if (taxSummary.length > 0) {
        const totals = taxSummary[taxSummary.length - 1]; // last object is totals
        totalRow = [
          "Total",
          CurrencyConverter.toCurrency(totals?.taxTotal || 0, "INR"),
          CurrencyConverter.toCurrency(totals?.sgstTotal || 0, "INR"),
          CurrencyConverter.toCurrency(totals?.cgstTotal || 0, "INR"),
          CurrencyConverter.toCurrency(totals?.igstTotal || 0, "INR"),
        ].join(",");
      }

      // Combine
      summaryBlock = [summaryHeader, ...summaryRows, totalRow]
        .filter(Boolean) // removes any empty lines
        .join("\n");
    }

    const csvContent = headers + rows + "\n" + totalRow + summaryBlock;

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const from = formatDate(fromDate);
    const to = formatDate(toDate);
    link.setAttribute("href", url);
    link.setAttribute("download", `HSN_Report_${from}_to_${to}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = async () => {
    setSelectedStore("");
  };

  useEffect(() => {
    handleListStore();
  }, []);

  return (
    <div>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="page-breadcrumb d-flex align-items-center paddingBottom">
            <div className="breadcrumb-title pe-3">HSN Report</div>
            <div className="ms-auto">
              <div className="dropdown">
                <button
                  className="btn btn-sm btn-export dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <DownloadIcon/> Export
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" onClick={exportToCSV}>
                      <SpreadSheetIcon/>{" "}
                      <span className="icon-text">Excel (CSV)</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-2">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label w-100">
                        From Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(fromDate)}
                          maxDate={dayjs(serverTime)}
                          format="DD MMM YYYY"
                          onChange={handleFromDateChange}
                          slotProps={{
                            textField: {
                              className: "form-control",
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer", 
                                },
                              },                            
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="col-12">
                      <label className="form-label w-100">
                        To Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(toDate)}
                          minDate={dayjs(toDateMin)}
                          maxDate={dayjs(toDateMax)}
                          format="DD MMM YYYY"
                          onChange={handleToDateChange}
                          slotProps={{
                            textField: {
                              className: "form-control",
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer", 
                                },
                              },                            
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* <div className="col-12">
                      <label className="form-label w-100">
                        Store <span className="text-danger"> *</span>
                      </label>
                      <Select
                        options={options}
                        onChange={handleSelectStore}
                        value={isSelectedStore}
                        ref={storeSelectedRef}
                        isSearchable
                        formatOptionLabel={renderOptionLabel}
                      />
                      {errors.store && (
                        <div className="invalid-feedback">{errors.store}</div>
                      )}
                    </div> */}

                    <div className="col-12">
                      <div className="d-grid">
                        {isbtnLoading ? (
                          <button
                            className="btn btn-primary pb-0 text-right"
                            disabled
                          >
                            <CircularProgress
                              size={22}
                              className="text-white"
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            id="salesreportbtn"
                            onClick={handleHsnReport}
                          >
                            Generate <ChevronRightIcon/>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-10">
              <div className="card p-3">
                <ViewHsnReport
                  isData={isHsnReportData}
                  isPreLoading={isPreLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HsnReport;
